import {useQuery} from '@tanstack/react-query'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {RestrictionRuleControllerViolationsEndpoint} from '@/types/endpoint-contracts'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {formatTimestamp} from '@/lib/date-utils'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {MutedText} from '@/components/shared/MutedText'
import {StatValue} from '@/components/shared/StatValue'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {PageLayout} from '@/components/shared/PageLayout'
import {useIsEmbedded} from '@/components/shared/EmbeddedContext'
import {Card, CardContent} from '@/components/ui/card'
import {Pagination} from '@/components/ui/pagination'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'

export function RestrictionRuleViolationsDashboard() {
    const i18n = useI18n()
    const services = useServices()
    const embedded = useIsEmbedded()

    usePageTitle(embedded ? undefined : i18n.restrictionRules.violationsDashboardTitle)

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: RestrictionRuleControllerViolationsEndpoint,
        defaultSortBy: RestrictionRuleControllerViolationsEndpoint.sort.DETECTED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data: violations, isLoading, error} = useQuery({
        queryKey: ['restriction-rule-violations-dashboard', queryString],
        queryFn: () => services.restrictionRules.violations(Object.fromEntries(new URLSearchParams(queryString))),
    })
    const totalItems = violations?.totalElements ?? 0
    const totalPages = violations?.totalPages ?? 0

    const filters = (
        <UnifiedSearchBar
            contract={RestrictionRuleControllerViolationsEndpoint}
            value={searchAndFilters}
            onChange={setSearchAndFilters}
            placeholder={i18n.restrictionRules.searchByIdentity}
            testIdNamespace="violations"
        />
    )

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.restrictionRuleViolation}
            title={i18n.restrictionRules.violationsDashboardTitle as string}
            description={i18n.restrictionRules.violationsDashboardSubtitle}
            count={violations?.totalElements}
            filters={filters}
            loading={isLoading}
            error={error}
            data-testid="violations-dashboard"
            stats={
                <DetailGrid columns={3}>
                    <Card>
                        <CardContent className="pt-6">
                            <MutedText size="sm" className="font-medium">{i18n.restrictionRules.totalOpenViolations}</MutedText>
                            <StatValue variant="danger" value={totalItems}/>
                        </CardContent>
                    </Card>
                </DetailGrid>
            }
            emptyState={
                <MutedText block centered>{i18n.restrictionRules.noViolationsFound}</MutedText>
            }
            pagination={
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            }
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-rule-name">{i18n.restrictionRules.ruleName}</TableHead>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-denied-profiles">{i18n.restrictionRules.deniedProfiles}</TableHead>
                        <TableHead data-testid="col-detected-at">{i18n.restrictionRules.detectedAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {violations?.content.map(v => (
                        <ClickableTableRow key={v.id} to={routes.accessGovernance.restrictionRules.detail(v.ruleId)}>
                            <TableCell>
                                <TextLink
                                    to={routes.accessGovernance.restrictionRules.detail(v.ruleId)}
                                    variant="bold"
                                    testId={`link-rule-${v.ruleId}`}
                                    stopPropagation
                                >
                                    {v.ruleName}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <EntityLink value={v.identity} stopPropagation testId={`link-identity-${v.identity.id}`}/>
                            </TableCell>
                            <TableCell>
                                <EntityLink value={v.violatingProfile} stopPropagation/>
                            </TableCell>
                            <TableCell>
                                <MutedText size="sm">{formatTimestamp(v.detectedAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
